import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './KingEffects.module.css';

const CROWN_COUNT = 26;

/** Longest a single crown takes to cross the viewport. */
const MAX_FALL_MS = 6000;
/** Latest a crown is allowed to start, so the burst stays dense rather than trickling. */
const MAX_DELAY_MS = 1800;

/**
 * A burst of crowns falling past the page when the reigning top donator's profile opens.
 *
 * Deliberately finite: it plays once on arrival and then removes itself, rather than looping for as
 * long as the visitor reads the page. Pure CSS keyframes, so the browser animates it off the main
 * thread and scrolling stays smooth. Visitors who asked for reduced motion get nothing.
 */
const CrownRain = () => {
    const [done, setDone] = useState(false);

    // Randomized once per mount — recomputing on each render would make crowns visibly jump.
    const crowns = useMemo(
        () =>
            Array.from({ length: CROWN_COUNT }, (_, i) => ({
                id: i,
                left: Math.random() * 100,
                duration: 3200 + Math.random() * (MAX_FALL_MS - 3200),
                delay: Math.random() * MAX_DELAY_MS,
                size: 14 + Math.random() * 18,
                drift: -40 + Math.random() * 80,
                spin: 180 + Math.random() * 360,
            })),
        []
    );

    // Unmount once the last crown has landed, so nothing is left animating behind the page.
    useEffect(() => {
        const timer = window.setTimeout(() => setDone(true), MAX_DELAY_MS + MAX_FALL_MS + 200);
        return () => window.clearTimeout(timer);
    }, []);

    if (done) return null;

    // Portalled to <body>: `position: fixed` resolves against the nearest ancestor with a transform
    // or filter rather than the viewport, and the profile card sits inside one (it uses
    // backdrop-filter). Rendering outside the page tree keeps the overlay pinned to the viewport.
    return createPortal(
        <div className={styles.crownRain} aria-hidden="true">
            {crowns.map(({ id, left, duration, delay, size, drift, spin }) => (
                <span
                    key={id}
                    className={styles.crown}
                    style={{
                        left: `${left}%`,
                        fontSize: `${size}px`,
                        animationDuration: `${duration}ms`,
                        animationDelay: `${delay}ms`,
                        ['--drift' as string]: `${drift}px`,
                        ['--spin' as string]: `${spin}deg`,
                    }}
                >
                    ♛
                </span>
            ))}
        </div>,
        document.body
    );
};

export default CrownRain;
